/**
 * @fileoverview Página para exportar informe de órdenes de producción a Excel
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { RepositoryFactory, ReservaInventarioRepository, SolicitudInventarioRepository } from '../../repositories';
import { validateDateRange, hasActiveFilters } from '../../helpers/informeOPValidations';
import { generateExcel } from '../../helpers/informeOPExcelHelper';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

const InformeOPPage = () => {
  document.title = 'Informe OP | GraphicForce - Printesco';

  // Estados de filtros
  const [filters, setFilters] = useState({
    estados: {
      finalizada: false,
      enProceso: false,
      enEdicion: false,
    },
    fechaCreacion: { inicio: null, fin: null },
    fechaInicio: { inicio: null, fin: null },
    fechaFinalizacion: { inicio: null, fin: null },
    fechaEntrega: { inicio: null, fin: null },
    trabajoInterno: 'todos',
    cliente: null,
    producto: null,
  });

  // Estado para filtro de reprocesos
  const [filtrarReprocesos, setFiltrarReprocesos] = useState(false);

  // Estados de validación
  const [dateErrors, setDateErrors] = useState({
    fechaCreacion: null,
    fechaInicio: null,
    fechaFinalizacion: null,
    fechaEntrega: null,
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(true);

  // Repositorios
  const ordenRepository = useMemo(() => RepositoryFactory.getRepository('orden_produccion'), []);
  const clienteRepository = useMemo(() => RepositoryFactory.getRepository('cliente'), []);
  const productoRepository = useMemo(() => RepositoryFactory.getRepository('producto'), []);
  const reservaRepository = useMemo(() => new ReservaInventarioRepository(), []);
  const solicitudRepository = useMemo(() => new SolicitudInventarioRepository(), []);

  // Cargar clientes al montar
  useEffect(() => {
    const loadClientes = async () => {
      try {
        setLoadingClientes(true);
        const clientesData = await clienteRepository.getClientesParaAutocompletado();
        setClientes(clientesData.map(c => ({ value: c.id, label: c.razon_social || '' })));
      } catch (err) {
        console.error('Error loading clientes:', err);
        setError('Error al cargar clientes');
      } finally {
        setLoadingClientes(false);
      }
    };

    loadClientes();
  }, [clienteRepository]);

  // Cargar productos al montar
  useEffect(() => {
    const loadProductos = async () => {
      try {
        setLoadingProductos(true);
        const productosData = await productoRepository.getProductosParaAutocompletado();
        setProductos(productosData.map(p => ({ value: p.id, label: p.nombre || '' })));
      } catch (err) {
        console.error('Error loading productos:', err);
        setError('Error al cargar productos');
      } finally {
        setLoadingProductos(false);
      }
    };

    loadProductos();
  }, [productoRepository]);

  // Validar filtros activos
  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);

  // Handlers para estados (checkboxes)
  const handleEstadoChange = (estado) => {
    setFilters(prev => ({
      ...prev,
      estados: {
        ...prev.estados,
        [estado]: !prev.estados[estado],
      },
    }));
    setError(null);
  };

  // Handler genérico para fechas
  const handleDateChange = (dateType, rangeType, date) => {
    const dateValue = date ? (Array.isArray(date) ? date[0] : date) : null;
    const dateString = dateValue ? new Date(dateValue).toISOString().split('T')[0] : null;

    setFilters(prev => {
      const newFilters = {
        ...prev,
        [dateType]: {
          ...prev[dateType],
          [rangeType]: dateString,
        },
      };

      // Validar rango de fechas
      if (dateType === 'fechaCreacion' || dateType === 'fechaInicio' || 
          dateType === 'fechaFinalizacion' || dateType === 'fechaEntrega') {
        const inicio = newFilters[dateType].inicio;
        const fin = newFilters[dateType].fin;

        if (inicio && fin) {
          const error = validateDateRange(inicio, fin);
          setDateErrors(prev => ({
            ...prev,
            [dateType]: error,
          }));
        } else {
          setDateErrors(prev => ({
            ...prev,
            [dateType]: null,
          }));
        }
      }

      return newFilters;
    });

    setError(null);
  };

  // Handler para trabajo interno
  const handleTrabajoInternoChange = (e) => {
    setFilters(prev => ({
      ...prev,
      trabajoInterno: e.target.value,
    }));
    setError(null);
  };

  // Handler para cliente
  const handleClienteChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      cliente: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para producto
  const handleProductoChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      producto: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para exportar
  const handleExport = async () => {
    // Validar que no haya errores de fecha
    const hasDateErrors = Object.values(dateErrors).some(err => err !== null);
    if (hasDateErrors) {
      setError('Por favor, corrija los errores en los rangos de fechas antes de exportar.');
      return;
    }

    // Validar que haya al menos un filtro activo
    if (!hasFilters) {
      setError('Por favor, seleccione al menos un filtro antes de exportar.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Preparar filtros para el repositorio (incluye filtro de reprocesos)
      const reporteFilters = {
        estados: filters.estados,
        fechaCreacion: filters.fechaCreacion,
        fechaInicio: filters.fechaInicio,
        fechaFinalizacion: filters.fechaFinalizacion,
        fechaEntrega: filters.fechaEntrega,
        trabajoInterno: filters.trabajoInterno,
        cliente: filters.cliente,
        producto: filters.producto,
        soloReprocesos: filtrarReprocesos,
      };

      // El filtro de reprocesos se aplica en el backend
      const ordenes = await ordenRepository.getOrdenesParaReporte(reporteFilters);

      if (ordenes.length === 0) {
        const mensaje = filtrarReprocesos
          ? 'No se encontraron órdenes de reproceso con los filtros seleccionados.'
          : 'No se encontraron órdenes con los filtros seleccionados.';
        setError(mensaje);
        setLoading(false);
        return;
      }

      // Usar los IDs de las órdenes ya filtradas por el backend
      const ordenIds = ordenes.map(o => o.id);
      const materialesPorOrden = new Map();

      const [reservasResult, solicitudesResult] = await Promise.all([
        reservaRepository.getByOrdenes(ordenIds),
        solicitudRepository.getByOrdenes(ordenIds)
      ]);

      const combinedAll = [
        ...(reservasResult?.items || []),
        ...(solicitudesResult?.items || [])
      ];

      combinedAll.forEach((item) => {
        const ordenId = item.orden_produccion || item.expand?.orden_produccion?.id;
        if (!ordenId) return;
        const list = materialesPorOrden.get(ordenId) || [];
        list.push(item);
        materialesPorOrden.set(ordenId, list);
      });

      generateExcel(ordenes, materialesPorOrden, filtrarReprocesos);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Error al generar el reporte. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs maintitle="Dashboard" title="Exportación" breadcrumbItem="Informe OP" />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Informe de Órdenes de Producción</h4>
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {!hasFilters && (
                  <Alert color="info" className="mb-3">
                    Por favor, seleccione al menos un filtro para continuar.
                  </Alert>
                )}

                <Form>
                  {/* Estados */}
                  <FormGroup>
                    <Label>Estado</Label>
                    <div>
                      <FormGroup check inline className="me-3">
                        <Input
                          type="checkbox"
                          id="estado-finalizada"
                          checked={filters.estados.finalizada}
                          onChange={() => handleEstadoChange('finalizada')}
                        />
                        <Label check htmlFor="estado-finalizada" className="ms-2">
                          Finalizada
                        </Label>
                      </FormGroup>
                      <FormGroup check inline className="me-3">
                        <Input
                          type="checkbox"
                          id="estado-en-proceso"
                          checked={filters.estados.enProceso}
                          onChange={() => handleEstadoChange('enProceso')}
                        />
                        <Label check htmlFor="estado-en-proceso" className="ms-2">
                          En Proceso
                        </Label>
                      </FormGroup>
                      <FormGroup check inline className="me-3">
                        <Input
                          type="checkbox"
                          id="estado-en-edicion"
                          checked={filters.estados.enEdicion}
                          onChange={() => handleEstadoChange('enEdicion')}
                        />
                        <Label check htmlFor="estado-en-edicion" className="ms-2">
                          En Edición
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Input
                          type="checkbox"
                          id="filtro-reprocesos"
                          checked={filtrarReprocesos}
                          onChange={(e) => setFiltrarReprocesos(e.target.checked)}
                        />
                        <Label check htmlFor="filtro-reprocesos" className="ms-2">
                          Solo Reprocesos
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>

                  <Row>
                    {/* Fecha de Creación */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Creación</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaCreacion.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaCreacion', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaCreacion.fin || ''}
                              onChange={(dates) => handleDateChange('fechaCreacion', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaCreacion && (
                          <div className="text-danger small mt-1">{dateErrors.fechaCreacion}</div>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Fecha de Inicio */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Inicio</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaInicio.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaInicio', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaInicio.fin || ''}
                              onChange={(dates) => handleDateChange('fechaInicio', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaInicio && (
                          <div className="text-danger small mt-1">{dateErrors.fechaInicio}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Fecha de Finalización */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Finalización</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaFinalizacion.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaFinalizacion', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaFinalizacion.fin || ''}
                              onChange={(dates) => handleDateChange('fechaFinalizacion', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaFinalizacion && (
                          <div className="text-danger small mt-1">{dateErrors.fechaFinalizacion}</div>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Fecha de Entrega */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Entrega</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaEntrega.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaEntrega', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaEntrega.fin || ''}
                              onChange={(dates) => handleDateChange('fechaEntrega', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaEntrega && (
                          <div className="text-danger small mt-1">{dateErrors.fechaEntrega}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Trabajo Interno */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="trabajo-interno">Trabajo Interno</Label>
                        <Input
                          type="select"
                          id="trabajo-interno"
                          value={filters.trabajoInterno}
                          onChange={handleTrabajoInternoChange}
                        >
                          <option value="todos">Todos</option>
                          <option value="si">Sí</option>
                          <option value="no">No</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    {/* Cliente */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="cliente">Cliente</Label>
                        <Select
                          id="cliente"
                          options={clientes}
                          isClearable
                          isSearchable
                          placeholder={loadingClientes ? 'Cargando...' : 'Seleccionar cliente'}
                          isLoading={loadingClientes}
                          value={clientes.find(c => c.value === filters.cliente) || null}
                          onChange={handleClienteChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Producto */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="producto">Producto</Label>
                        <Select
                          id="producto"
                          options={productos}
                          isClearable
                          isSearchable
                          placeholder={loadingProductos ? 'Cargando...' : 'Seleccionar producto'}
                          isLoading={loadingProductos}
                          value={productos.find(p => p.value === filters.producto) || null}
                          onChange={handleProductoChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Botón Exportar */}
                  <FormGroup>
                    <Button
                      color="primary"
                      onClick={handleExport}
                      disabled={!hasFilters || loading || Object.values(dateErrors).some(err => err !== null)}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        'Exportar a Excel'
                      )}
                    </Button>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InformeOPPage;
